import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { cn } from '../utils/utils';
import { moveId } from '../utils/manualOrder';

export interface ReorderableItem {
    id: string;
    label: string;
    /** A second line, e.g. a note on a fixed row. */
    hint?: string;
}

interface ReorderableListProps {
    items: ReorderableItem[];
    /** The ids in their current order — the draft while editing. */
    order: string[];
    /** False: the order is shown, locked (the view of an EditableSection). */
    editing: boolean;
    onChange: (order: string[]) => void;
    /** Rows shown first, never movable (e.g. the standard category). */
    pinned?: ReorderableItem[];
    /** Accessible name of the list. */
    label: string;
}

/**
 * A list whose order the user arranges (#3016): native HTML5 drag and drop
 * plus "move up" / "move down" buttons for keyboard and touch users — no
 * drag library. It only reports the new order; saving belongs to the
 * `EditableSection` around it, so nothing is stored on a drop.
 */
const ReorderableList = ({
    items, order, editing, onChange, pinned = [], label,
}: ReorderableListProps) => {
    const { t } = useTranslation();
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [overIndex, setOverIndex] = useState<number | null>(null);
    const byId = new Map(items.map((item) => [item.id, item]));
    const rows = order.map((id) => byId.get(id)).filter((item): item is ReorderableItem => !!item);

    const endDrag = () => {
        setDragIndex(null);
        setOverIndex(null);
    };

    const iconButton = 'p-1 rounded-[5px] text-foreground/60 hover:text-foreground hover:bg-foreground/5'
        + ' disabled:opacity-30 disabled:hover:bg-transparent';

    return (
        <ol aria-label={label} className={'flex flex-col'}>
            {pinned.map((item) => (
                <li
                    key={item.id}
                    className={'flex items-center gap-3 py-2 border-b border-foreground/5 text-sm'}
                >
                    <span className={'w-5 shrink-0'} />
                    <div className={'min-w-0 flex-grow'}>
                        <p className={'text-foreground truncate'}>{item.label}</p>
                        {item.hint && <p className={'text-xs text-foreground/60'}>{item.hint}</p>}
                    </div>
                </li>
            ))}
            {rows.map((item, index) => (
                <li
                    key={item.id}
                    draggable={editing}
                    onDragStart={(event) => {
                        if (!editing) return;
                        setDragIndex(index);
                        event.dataTransfer.effectAllowed = 'move';
                        // Firefox starts a drag only with data set.
                        event.dataTransfer.setData('text/plain', item.id);
                    }}
                    onDragOver={(event) => {
                        if (!editing || dragIndex === null) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        if (overIndex !== index) setOverIndex(index);
                    }}
                    onDrop={(event) => {
                        if (!editing || dragIndex === null) return;
                        event.preventDefault();
                        onChange(moveId(order, order.indexOf(rows[dragIndex].id), order.indexOf(item.id)));
                        endDrag();
                    }}
                    onDragEnd={endDrag}
                    className={cn(
                        'flex items-center gap-3 py-2 border-b border-foreground/5 last:border-b-0 text-sm',
                        editing && 'cursor-grab',
                        dragIndex === index && 'opacity-50',
                        overIndex === index && dragIndex !== index && 'bg-foreground/5',
                    )}
                >
                    <span className={'w-5 shrink-0 text-foreground/40'} aria-hidden={true}>
                        {editing ? <GripVertical size={16} /> : `${pinned.length + index + 1}.`}
                    </span>
                    <div className={'min-w-0 flex-grow'}>
                        <p className={'text-foreground truncate'}>{item.label}</p>
                        {item.hint && <p className={'text-xs text-foreground/60'}>{item.hint}</p>}
                    </div>
                    {editing && (
                        <div className={'flex shrink-0 gap-1'}>
                            <button
                                type={'button'}
                                className={iconButton}
                                disabled={index === 0}
                                aria-label={t('services.manual_order.move_up', { name: item.label })}
                                title={t('services.manual_order.move_up', { name: item.label })}
                                onClick={() => onChange(moveId(order, order.indexOf(item.id), order.indexOf(item.id) - 1))}
                            >
                                <ChevronUp size={16} />
                            </button>
                            <button
                                type={'button'}
                                className={iconButton}
                                disabled={index === rows.length - 1}
                                aria-label={t('services.manual_order.move_down', { name: item.label })}
                                title={t('services.manual_order.move_down', { name: item.label })}
                                onClick={() => onChange(moveId(order, order.indexOf(item.id), order.indexOf(item.id) + 1))}
                            >
                                <ChevronDown size={16} />
                            </button>
                        </div>
                    )}
                </li>
            ))}
        </ol>
    );
};

export default ReorderableList;
